import { z } from 'zod';
import { cancellationReasonSchema } from './visit.js';
/**
 * Bulk operations on visits (§14.6, slice-03 session 11).
 *
 * "All bulk operations run as background jobs with a preview-before-commit
 * step, a per-row result report, and full rollback on abort." So a request
 * starts a job and answers 202; the job previews every row through the same
 * fifteen rules a single booking meets; the coordinator reads the rows and
 * commits; the commit applies every ready row in one transaction or none.
 *
 * Bulk never overrides a rule. A refused row stays refused and is handled on
 * its own, where an override has a person, a rule and a reason.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-06 — four digits, two, two.');
const range = { from: isoDate, to: isoDate };
const ordered = (body) => body.to >= body.from;
const orderedMessage = { path: ['to'], message: 'The last day can’t be before the first.' };
/** A caregiver's visits to someone else — or to nobody, as open shifts. Termination, leave. */
export const bulkReassignSchema = z
    .object({
    operation: z.literal('REASSIGN'),
    caregiverId: z.string().uuid(),
    /** Null: leave them open for the board. */
    toCaregiverId: z.string().uuid().nullable(),
    ...range,
})
    .refine(ordered, orderedMessage)
    .refine((body) => body.toCaregiverId !== body.caregiverId, {
    path: ['toCaregiverId'],
    message: 'Choose someone else, or leave them open.',
});
/** A client's visits cancelled for a stretch — a hospital stay. */
export const bulkCancelSchema = z
    .object({
    operation: z.literal('CANCEL'),
    clientId: z.string().uuid(),
    ...range,
    reasonCode: cancellationReasonSchema,
    note: z.string().trim().max(2_000).nullish(),
})
    .refine(ordered, orderedMessage);
/** One week's one-off visits repeated into another. Pattern visits are the pattern's to repeat. */
export const bulkCopyWeekSchema = z
    .object({
    operation: z.literal('COPY_WEEK'),
    /** Any day in the week copied from. */
    fromWeekOf: isoDate,
    /** Any day in the week copied into. */
    toWeekOf: isoDate,
})
    .refine((body) => body.toWeekOf > body.fromWeekOf, {
    path: ['toWeekOf'],
    message: 'Copy into a later week.',
});
export const bulkVisitRequestSchema = z.union([
    bulkReassignSchema,
    bulkCancelSchema,
    bulkCopyWeekSchema,
]);
export const bulkVisitJobStatusSchema = z.enum([
    'PREVIEWING',
    'READY',
    'COMMITTING',
    'COMMITTED',
    'FAILED',
    'DISCARDED',
]);
/**
 * One row of the report.
 *
 * READY: will be written on commit. REFUSED: a rule says no — the reasons are
 * the engine's words. SKIPPED: nothing to do (already there, already begun).
 * After a commit, DONE or — if the commit was abandoned — the row's reason.
 */
export const bulkVisitRowSchema = z.object({
    key: z.string(),
    /** The visit changed; null for a copy not yet written. */
    visitId: z.string().uuid().nullable(),
    outcome: z.enum(['READY', 'REFUSED', 'SKIPPED', 'DONE']),
    clientName: z.string(),
    caregiverName: z.string().nullable(),
    localDate: isoDate,
    localStartTime: z.string(),
    localEndTime: z.string(),
    /** What commit does to it, in words. */
    action: z.string(),
    reasons: z.array(z.string()),
    warnings: z.array(z.string()),
});
export const bulkVisitJobSchema = z.object({
    id: z.string().uuid(),
    operation: z.enum(['REASSIGN', 'CANCEL', 'COPY_WEEK']),
    status: bulkVisitJobStatusSchema,
    /** Why a commit was abandoned — and nothing was written. */
    failure: z.string().nullable(),
    counts: z.object({
        ready: z.number().int(),
        refused: z.number().int(),
        skipped: z.number().int(),
        done: z.number().int(),
    }),
    rows: z.array(bulkVisitRowSchema),
    createdAt: z.string().datetime(),
    committedAt: z.string().datetime().nullable(),
});
export const bulkVisitJobStartedSchema = z.object({
    jobId: z.string().uuid(),
    status: bulkVisitJobStatusSchema,
});
//# sourceMappingURL=bulk-visits.js.map